import { isSessionDraftToolName } from '../../../contracts/approval-kinds';
import type {
  ApprovalResponse,
  ChatItem,
  ChatItemIdentity,
  ChildAgentRunMetadata,
  PendingApproval,
  ToolCallBlock,
  ToolExecutionStatus,
  ToolResultBlock,
  ToolUseItem,
  ToolUseStatus,
} from '../../../contracts/harness.types';
import {
  parseToolCallDisplay,
  readToolCallDisplay,
  type ToolCallDisplay,
} from '../../../contracts/tool-call-display';
import { readChildAgentRunMetadata } from '../child-runs/metadata';
import { readRecord } from '../metadata-readers';
import { deriveIsComputerUse } from '../timeline/derive-is-computer-use';
import { deriveIsComputerUseNotePush } from '../timeline/derive-is-computer-use-note-push';
import { observeFrame, presentIdentity } from './identity';
import { toolKey } from './keys';
import type {
  LiveToolStatus,
  PositionalTranscriptEntry,
  ToolTranscriptEntry,
  TranscriptStore,
} from './types';

export interface FoldTranscriptOptions {
  /**
   * Drop pre-output cancelled turns (the user bubble + its `turn_cancelled`
   * marker) so a stop-before-output leaves no trace — paired with the composer
   * returning the prompt to the input. Opt-in; other surfaces keep the
   * "Stopped" marker.
   */
  dropEmptyCancelledTurns?: boolean;
}

/** The pure render. No merging, de-duplication or source arbitration — all of
 *  that happened at ingestion. */
export const foldTranscript = (
  store: TranscriptStore,
  { dropEmptyCancelledTurns = false }: FoldTranscriptOptions = {}
): ChatItem[] => {
  const dropped = dropEmptyCancelledTurns ? emptyCancelledEntryIds(store) : EMPTY_ID_SET;
  const seenToolCallIds = new Set<string>();
  const items: ChatItem[] = [];

  for (const entryId of store.order) {
    if (dropped.has(entryId)) continue;
    const entry = store.byId[entryId];
    if (!entry || entry.kind === 'tool' || entry.kind === 'prompt_context_failure') {
      continue;
    }
    appendEntryItems({ entry, store, items, seenToolCallIds });
  }

  for (const toolEntryId of store.toolOrder) {
    const entry = store.byId[toolEntryId];
    if (entry?.kind !== 'tool') continue;
    if (seenToolCallIds.has(entry.toolCallId)) continue;
    const approval = entry.pendingApproval;
    if (entry.pendingApprovalStale) continue;
    if (approval) {
      items.push({
        kind: 'tool_use',
        id: approval.tool_call_id,
        name: approval.tool_name,
        input: approval.tool_input,
        inputMerge: approval.tool_input_merge,
        status: 'pending_approval',
        pendingApprovalId: approval.approval_id,
        ...(approval.available_override_scopes && {
          availableOverrideScopes: approval.available_override_scopes,
        }),
        ...(approval.toolCallDisplay && { toolCallDisplay: approval.toolCallDisplay }),
        isComputerUse: deriveIsComputerUse({ toolName: approval.tool_name }),
        isComputerUseNotePush: deriveIsComputerUseNotePush({
          toolName: approval.tool_name,
        }),
        ...presentIdentity({
          turnId: entry.turnId,
          iteration: entry.iteration,
          seenAtMs: entry.seenAtMs,
        }),
      });
      continue;
    }
    if (!entry.livePartialInput && !entry.liveToolName) continue;
    const name = entry.liveToolName ?? 'tool';
    if (!isSessionDraftToolName(name)) continue;
    items.push({
      kind: 'tool_use',
      id: entry.toolCallId,
      name,
      input: {},
      status: 'streaming_input',
      ...(entry.livePartialInput ? { partialInput: entry.livePartialInput } : {}),
      isComputerUse: deriveIsComputerUse({ toolName: name }),
      isComputerUseNotePush: deriveIsComputerUseNotePush({ toolName: name }),
      ...presentIdentity({
        turnId: entry.turnId,
        iteration: entry.iteration,
        seenAtMs: entry.seenAtMs,
      }),
    });
  }

  for (const failureId of store.failureOrder) {
    const entry = store.byId[failureId];
    if (entry?.kind !== 'prompt_context_failure') continue;
    items.push({
      kind: 'prompt_context_failed',
      id: entry.eventId,
      message: entry.text,
    });
  }

  return items;
};

const EMPTY_ID_SET: ReadonlySet<string> = new Set<string>();

/** A `turn_cancelled` marker whose turn produced no output: the entry before it
 *  is the prompt. A post-output cancel keeps its transcript + Stopped note. */
const emptyCancelledEntryIds = (store: TranscriptStore): ReadonlySet<string> => {
  const dropped = new Set<string>();
  store.order.forEach((entryId, index) => {
    const marker = store.byId[entryId];
    if (marker?.kind !== 'marker' || marker.marker !== 'turn_cancelled') return;
    const previousId = store.order[index - 1];
    if (!previousId || store.byId[previousId]?.kind !== 'user') return;
    dropped.add(entryId);
    dropped.add(previousId);
  });
  return dropped;
};

interface AppendEntryItemsArgs {
  entry: PositionalTranscriptEntry;
  store: TranscriptStore;
  items: ChatItem[];
  seenToolCallIds: Set<string>;
}

const appendEntryItems = ({
  entry,
  store,
  items,
  seenToolCallIds,
}: AppendEntryItemsArgs): void => {
  const identity: ChatItemIdentity = presentIdentity({
    turnId: entry.turnId,
    iteration: entry.iteration,
    createdAt: entry.createdAt,
    ...(entry.kind === 'assistant' ? { seenAtMs: entry.seenAtMs } : {}),
  });

  if (entry.kind === 'marker') {
    if (entry.marker === 'turn_cancelled') {
      items.push({
        kind: 'turn_cancelled_marker',
        id: `cancelled:${entry.id}`,
        ...identity,
      });
    } else if (entry.marker === 'turn_failed') {
      items.push({
        kind: 'turn_failed',
        id: `failed:${entry.id}`,
        reason: entry.reason,
        ...identity,
      });
    }
    return;
  }

  if (entry.kind === 'user') {
    const text = entry.message.blocks
      .filter((block) => block.kind === 'text')
      .map((block) => block.text)
      .join('\n');
    items.push({
      kind: 'user_message',
      id: `user:${entry.id}`,
      text,
      attachments: entry.message.blocks.filter((block) => block.kind === 'attachment'),
      systemGenerated: entry.message.system_generated,
      ...identity,
    });
    return;
  }

  entry.message.blocks.forEach((block, blockIndex) => {
    const blockStreaming =
      entry.streaming && blockIndex === entry.message.blocks.length - 1;
    const blockIdentity = presentIdentity({
      turnId: entry.turnId,
      iteration: entry.iteration,
      createdAt: entry.createdAt,
      seenAtMs: entry.blockSeenAtMs?.[blockIndex] ?? entry.seenAtMs,
    });

    if (block.kind === 'text') {
      items.push({
        kind: 'assistant_text',
        id: `assistant-text:${entry.id}:${blockIndex}`,
        text: block.text,
        streaming: blockStreaming,
        ...blockIdentity,
      });
      return;
    }
    if (block.kind === 'thinking') {
      items.push({
        kind: 'thinking',
        id: `thinking:${entry.id}:${blockIndex}`,
        text: block.text,
        streaming: blockStreaming,
        ...blockIdentity,
      });
      return;
    }
    if (block.kind === 'tool_call') {
      if (seenToolCallIds.has(block.id)) return;
      seenToolCallIds.add(block.id);
      items.push(toolCallToItem(block, toolEntryFor(store, block.id), blockIdentity));
    }
  });
};

const toolEntryFor = (
  store: TranscriptStore,
  toolCallId: string
): ToolTranscriptEntry | undefined => {
  const entryId = store.keys[toolKey(toolCallId)];
  const entry = entryId ? store.byId[entryId] : undefined;
  return entry?.kind === 'tool' ? entry : undefined;
};

const toolCallToItem = (
  block: ToolCallBlock,
  tool: ToolTranscriptEntry | undefined,
  identity: ChatItemIdentity
): ToolUseItem => {
  const result = tool?.result;
  const approval = tool?.approval;
  const pendingApproval = tool?.pendingApprovalStale ? undefined : tool?.pendingApproval;
  const liveOutput = tool?.liveOutput;
  const persistedChildRun: ChildAgentRunMetadata | null = result
    ? readChildAgentRunMetadata(result.provider_metadata)
    : null;
  const effectiveChildRun = persistedChildRun ?? tool?.childRun;
  // While an approval is pending, the approval's display wins: a tool's
  // pre-approval hook replaces the model-authored display with a server one
  // (e.g. naming the patient and session), and without a hook the two match.
  // Otherwise prefer the tool_call block's display, then the approval context
  // — a legacy tool call may carry the display only on the approval context,
  // and the client no longer humanizes the raw tool name.
  const toolCallDisplay = pendingApproval
    ? (pendingApproval.toolCallDisplay ??
      parseToolCallDisplay(approval?.context?.tool_call_display) ??
      readItemToolCallDisplay(block))
    : (readItemToolCallDisplay(block) ??
      parseToolCallDisplay(approval?.context?.tool_call_display));

  return {
    ...identity,
    kind: 'tool_use',
    id: block.id,
    name: block.name,
    input: block.input,
    inputMerge: pendingApproval?.tool_input_merge ?? block.input_merge,
    status: effectiveStatus({
      result,
      approval,
      pendingApproval,
      liveStatus: tool?.liveStatus,
      livePartialInput: tool?.livePartialInput,
      persistedStatus: tool?.persistedStatus,
    }),
    ...(tool?.livePartialInput && { partialInput: tool.livePartialInput }),
    ...(liveOutput && { live_output: liveOutput }),
    ...(result && {
      result: {
        content: result.content,
        is_error: result.is_error,
        attachments: result.attachments,
      },
      result_provider_metadata: result.provider_metadata,
    }),
    ...(approval && { approval }),
    ...(pendingApproval && { pendingApprovalId: pendingApproval.approval_id }),
    ...(pendingApproval?.approval_kind && {
      pendingApprovalKind: pendingApproval.approval_kind,
    }),
    ...(pendingApproval?.context && {
      pendingApprovalContext: pendingApproval.context,
    }),
    ...(toolCallDisplay && { toolCallDisplay }),
    ...(block.provider_metadata && { provider_metadata: block.provider_metadata }),
    ...(effectiveChildRun && {
      childAgentRunId: effectiveChildRun.childAgentRunId,
      childAgentName: effectiveChildRun.childAgentName,
      childTurnId: effectiveChildRun.childTurnId,
      childPersistent: effectiveChildRun.persistent,
      isSubAgent: true,
    }),
    isComputerUse: deriveIsComputerUse({
      toolName: block.name,
      approvalKind: approval?.approval_kind,
    }),
    isComputerUseNotePush: deriveIsComputerUseNotePush({ toolName: block.name }),
    ...presentIdentity({
      turnId: identity.turnId ?? tool?.turnId,
      iteration: identity.iteration ?? tool?.iteration,
      // Call observed on the assistant entry, execution on the tool entry — the item spans both.
      seenAtMs: observeFrame(
        observeFrame(identity.seenAtMs, tool?.seenAtMs?.first),
        tool?.seenAtMs?.last
      ),
    }),
    ...(tool?.resultCreatedAt !== undefined && { resultCreatedAt: tool.resultCreatedAt }),
    ...(tool?.terminalOutcome !== undefined && { terminalOutcome: tool.terminalOutcome }),
  };
};

const readItemToolCallDisplay = (block: ToolCallBlock): ToolCallDisplay | undefined =>
  readToolCallDisplay(block.provider_metadata);

interface EffectiveStatusInput {
  result?: ToolResultBlock;
  approval?: ApprovalResponse;
  pendingApproval?: PendingApproval;
  liveStatus?: LiveToolStatus;
  livePartialInput?: string;
  persistedStatus?: ToolExecutionStatus;
}

const effectiveStatus = ({
  result,
  approval,
  pendingApproval,
  liveStatus,
  livePartialInput,
  persistedStatus,
}: EffectiveStatusInput): ToolUseStatus => {
  // Snapshot approval is authoritative — a stale live pendingApproval
  // must not override a resolved snapshot verdict.
  const snapshotResolved = approval != null && approval.status !== 'pending';
  if (!snapshotResolved && (pendingApproval || approval?.status === 'pending'))
    return 'pending_approval';
  const persisted = persistedToolStatus(persistedStatus, result);
  if (persisted) return persisted;
  if (result) {
    if (isCancelledToolResult(result)) return 'cancelled';
    return result.is_error ? 'error' : 'complete';
  }
  if (snapshotResolved)
    return approval?.status === 'approved' ? (liveStatus ?? 'running') : 'error';
  if (liveStatus) return liveStatus;
  if (livePartialInput) return 'streaming_input';
  return 'queued';
};

const persistedToolStatus = (
  status: ToolExecutionStatus | undefined,
  result?: ToolResultBlock
): ToolUseStatus | undefined => {
  if (!status) return undefined;
  if (status === 'cancelled') return 'cancelled';
  // A completed execution can still carry a blocked or failed task outcome,
  // which the backend collapses into the result's `is_error`.
  if (status === 'completed') return result?.is_error ? 'error' : 'complete';
  return 'error';
};

/** Fallback for runs persisted before the backend stamped a first-class status. */
const isCancelledToolResult = (result: ToolResultBlock): boolean =>
  metadataRecords(result.provider_metadata).some(
    (metadata) =>
      readString(metadata.status) === 'cancelled' ||
      readString(metadata.error_code) === 'USER_CANCELLED'
  );

const metadataRecords = (
  metadata: Record<string, unknown> | undefined
): Record<string, unknown>[] => {
  if (!metadata) return [];
  const additionalMetadata = readRecord(metadata.additional_metadata);
  const data = readRecord(metadata.data);
  const additionalData = readRecord(additionalMetadata?.data);

  return [metadata, additionalMetadata, data, additionalData].filter(
    (record): record is Record<string, unknown> => record !== null
  );
};

const readString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() ? value.trim() : undefined;
